/**
 * Browser adapter (posthog-js) for the landing site and the Electron renderers.
 *
 * Session replay and exception autocapture are project-side toggles in PostHog,
 * so they are not configured here. This module owns initialization, identity
 * and the sink contract.
 */
import posthog, { type PostHog, type PostHogConfig } from 'posthog-js'

import { POSTHOG_PROJECT_KEY, POSTHOG_PROXY_HOST, POSTHOG_UI_HOST } from './config'
import { evaluatesFlags, localSink, normalizeEnvironment, sendsEvents } from './environment'
import { createFlagReader, fallbackClientFlags, type ClientFlags, type FlagLookup } from './flags'
import {
  type AnalyticsClient,
  type AnalyticsClientOptions,
  type EnvironmentOptions,
  type IdentityHandoffStore,
  type AnalyticsProperties,
  type AnalyticsSink,
  type AnalyticsSurface,
  createAnalytics,
  noopAnalytics,
} from './core'

export type { AnalyticsClient, AnalyticsIdentity, AnalyticsProperties, AnalyticsSurface } from './core'

export interface BrowserAnalyticsOptions extends EnvironmentOptions {
  /** Overrides the default localStorage handoff, e.g. for the Electron renderer. */
  handoffStore?: IdentityHandoffStore
  /** Called instead of the sinks when the environment does not send. */
  onEvent?: AnalyticsClientOptions['onEvent']
  surface: AnalyticsSurface
  apiKey?: string
  host?: string
  baseProperties?: AnalyticsProperties | (() => AnalyticsProperties)
  /**
   * Reuse an already-initialized instance (Next.js `PostHogProvider`, or a
   * renderer that shares the window's client) instead of initializing here.
   */
  instance?: PostHog
  /**
   * The signed-in user, when the surface has one.
   *
   * posthog-js keeps owning the distinct id; this supplies the one part of
   * identity it cannot know. Without it `userId` is absent from every browser
   * event, so `$user_id` is set on the server surfaces and silently missing on
   * the browser ones, and an insight filtered on it drops the whole client
   * side while looking healthy.
   *
   * Read at capture time, since a surface signs in long after this is built.
   */
  getUserId?: () => string | undefined
  /**
   * Starts posthog-js on an id we already hold instead of letting it mint one.
   *
   * The Electron renderers need this: the main process resolves one PostHog
   * device id for the whole app, and a renderer left to invent its own would
   * make the same person two anonymous people until they sign in. Only applies
   * on first run, since posthog-js keeps the id it has persisted.
   */
  bootstrapDistinctId?: string
  /** Escape hatch for per-surface posthog-js config. */
  configOverrides?: Partial<PostHogConfig>
  onError?: (error: unknown, context: { sink: string; operation: string }) => void
}

function createSink(client: PostHog): AnalyticsSink {
  return {
    name: 'posthog-browser',

    capture(event, identity) {
      // posthog-js owns the distinct id in the browser: it persists one across
      // page loads and rotates it on identify, so overriding it would fight the
      // SDK. `userId` still has to ride along, or an insight filtering on
      // $user_id silently excludes every browser surface while keeping the
      // server ones.
      client.capture(
        event.name,
        identity.userId ? { ...event.properties, $user_id: identity.userId } : event.properties,
      )
    },

    captureException(error, properties) {
      // posthog-js builds the `$exception_list` that error tracking groups on.
      client.captureException(error, properties)
    },

    identify(identity, properties) {
      client.identify(identity.distinctId, properties)
    },

    alias(identity, previousId) {
      client.alias(identity.distinctId, previousId)
    },

    reset() {
      // posthog-js persists the identified distinct id in localStorage and a
      // cookie, and clears it only here. Without this a signed-out device keeps
      // attributing to the account that left, and the next person to sign in on
      // it inherits that id until their own identify lands.
      client.reset()
    },

    // No `flush`. posthog-js batches on its own timer and on `pagehide` and
    // exposes nothing await-able. Forcing a drain would mean emitting a
    // synthetic event, which is worse than letting the SDK finish on its own.
  }
}

/**
 * The config every Highlight browser surface initializes with.
 *
 * Exported so the defaults that matter can be asserted in a test. Two of them
 * are load-bearing rather than cosmetic, and both override a PostHog project
 * setting that is switched on server-side.
 */
export function browserConfig(
  options: Pick<
    BrowserAnalyticsOptions,
    'host' | 'configOverrides' | 'bootstrapDistinctId' | 'environment' | 'forceSend' | 'localSink'
  >,
): Partial<PostHogConfig> {
  const sink = localSink(options)
  return {
    api_host: sink ?? options.host ?? POSTHOG_PROXY_HOST,
    ui_host: POSTHOG_UI_HOST,
    // `isIdentifiedID: false` keeps the bootstrapped id anonymous, so a later
    // sign-in can still merge it into the account. Marking it identified is
    // what makes that merge illegal.
    ...(options.bootstrapDistinctId
      ? { bootstrap: { distinctID: options.bootstrapDistinctId, isIdentifiedID: false } }
      : {}),
    // Highlight names its own events. Autocapture would bury them under
    // `$autocapture` noise and make a breakdown by button name useless.
    autocapture: false,
    capture_pageview: false,
    // The project has `autocapture_exceptions_opt_in` on, and posthog-js honours
    // that remote setting unless the client overrides it. Left on, it would
    // vacuum up the `console.error` lines that sit above deliberate captures.
    // Where Sentry runs, its events reach PostHog through `mirrorSentryEvent`.
    capture_exceptions: false,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    // Lets a flag be scoped to environments in the PostHog UI.
    evaluation_contexts: [normalizeEnvironment(options.environment)],
    ...options.configOverrides,
    // After the overrides, so nothing can switch it back on by accident.
    //
    // posthog-js sends on its own once initialized: `capture_pageleave` posts
    // a `$pageleave` with no wrapper involvement, and init reaches out for
    // flags and whatever the project enables server-side, session replay
    // included. Gating our own `capture` leaves all of that untouched, so a
    // dev or test session still writes into the one project every environment
    // shares. The key is compiled in, so there is nothing else stopping it.
    ...(sendsEvents(options) ? {} : { opt_out_capturing_by_default: true, disable_session_recording: true }),
    // Flags load in development too: a read writes nothing, and the opt-out
    // above drops the exposures. A sink takes events only, so flags, remote
    // config and the recorder would all be fetched from a fake host.
    ...(evaluatesFlags(options) && !sink ? {} : { advanced_disable_decide: true }),
    ...(sink ? { disable_session_recording: true } : {}),
  }
}

/**
 * Initializes posthog-js (once) and returns a client.
 *
 * Returns `noopAnalytics` outside a browser so server-rendered imports in Next
 * don't need a `typeof window` guard at every call site.
 */
/** A browser analytics client and the flags of the posthog-js instance it initialized. */
export type BrowserAnalyticsClient = AnalyticsClient & { flags: ClientFlags }

export function createBrowserAnalytics(options: BrowserAnalyticsOptions): BrowserAnalyticsClient {
  if (typeof window === 'undefined') {
    return { ...noopAnalytics, flags: fallbackClientFlags }
  }

  const client = options.instance ?? posthog
  const apiKey = options.apiKey ?? POSTHOG_PROJECT_KEY

  // Applied whether or not the instance came from the caller: the defaults are
  // load-bearing, and skipping them on the reuse path would let the project's
  // `autocapture_exceptions_opt_in` turn exception capture back on. A second
  // init on an already-loaded client is a no-op in posthog-js, so whoever
  // initializes first wins — surfaces that share an instance must pass it
  // before anything else touches it.
  const config = browserConfig(options)
  if (!client.__loaded) {
    client.init(apiKey, config)
  }

  const analytics = createAnalytics({
    sinks: [createSink(client)],
    surface: options.surface,
    environment: options.environment,
    forceSend: options.forceSend,
    localSink: options.localSink,
    onEvent: options.onEvent,
    baseProperties: options.baseProperties,
    getIdentity: () => {
      // posthog-js owns the distinct id: it persists one across page loads and
      // rotates it on identify, so overriding it would fight the SDK. Only the
      // user id comes from the caller.
      const userId = options.getUserId?.()
      return userId ? { distinctId: client.get_distinct_id(), userId } : { distinctId: client.get_distinct_id() }
    },
    onError: options.onError,
  })

  // Where events don't send, the core client ignores identity. posthog-js
  // still decides who flags evaluate for, so without this a dev build
  // evaluates for the anonymous id while the server uses the user id. It is
  // opted out there, so identify and reset change local state and send nothing.
  const localIdentity: Partial<AnalyticsClient> = sendsEvents(options)
    ? {}
    : {
        identify: (identity) => identity.distinctId && client.identify(identity.distinctId),
        signIn: (identity) => identity.distinctId && client.identify(identity.distinctId),
        signOut: () => client.reset(),
      }

  return Object.assign(analytics, localIdentity, {
    flags: config.advanced_disable_decide ? fallbackClientFlags : browserFlags(client),
  })
}

/**
 * posthog-js refreshes flags every 5 minutes, backing off to an hour while
 * idle, so a window can lag the server by that long. Refreshing when a window
 * gains focus or comes into view catches it up before the user acts. The floor
 * stops focus flapping from turning into a request per switch; each one is
 * billed.
 */
const REFRESH_FLOOR_MS = 30_000

/**
 * posthog-js skips its refreshes while a window is hidden, and a desktop
 * window can stay hidden for days. This keeps a hidden window's flags from
 * going stale indefinitely. Background work that a flag must be able to stop
 * should still read server flags, which are always current.
 */
const HIDDEN_REFRESH_INTERVAL_MS = 30 * 60_000

/** Flags from posthog-js, which fetches them all in one request at init and on identify. */
function browserFlags(instance: PostHog): ClientFlags {
  // Starts now because init has just fetched.
  let lastRefresh = Date.now()
  const refresh = () => {
    if (Date.now() - lastRefresh >= REFRESH_FLOOR_MS) {
      lastRefresh = Date.now()
      instance.reloadFeatureFlags()
    }
  }

  window.addEventListener('focus', refresh)
  // posthog-js refreshes on becoming visible only once its 5 minutes are due.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refresh()
    }
  })
  setInterval(() => {
    if (document.visibilityState === 'hidden') {
      instance.reloadFeatureFlags()
    }
  }, HIDDEN_REFRESH_INTERVAL_MS)

  // Who the last loaded flags were evaluated for. `identify` and `reset`
  // change the distinct id at once, so the flags are not ready again until
  // that person's reload lands. Registered before any `onChange` listener, so
  // it is current by the time those re-render.
  let loadedFor: string | undefined
  instance.onFeatureFlags(() => {
    loadedFor = instance.get_distinct_id()
  })

  // Before the first response, with nothing persisted, posthog-js warns on
  // every read. A React tree reads on every render, and the answer is the
  // fallback anyway.
  const loaded = () => {
    const featureFlags = instance.featureFlags
    return Boolean(featureFlags && (featureFlags.hasLoadedFlags || featureFlags.getFlags().length > 0))
  }

  const lookup: FlagLookup = (key, { expose }) =>
    loaded() ? instance.getFeatureFlagResult(key, { send_event: expose }) : undefined

  return {
    ...createFlagReader(lookup),
    lookup,
    isReady: () => loadedFor !== undefined && loadedFor === instance.get_distinct_id(),
    onChange: (listener) => instance.onFeatureFlags(() => listener()),
    refresh,
  }
}

/**
 * Keeps the outgoing account id across a sign-out.
 *
 * Deliberately not the same storage posthog-js resets: the point is to outlive
 * the reset. Scoped to the device, never sent anywhere except as the
 * `previous_logged_in_user_id` property on the next sign-in.
 */
export function browserHandoffStore(key = 'highlight_previous_user_id'): IdentityHandoffStore {
  return {
    read: () => {
      try {
        return window.localStorage.getItem(key) ?? undefined
      } catch {
        // Private mode, or storage disabled. Linking is a nice-to-have.
        return undefined
      }
    },
    write: (userId) => {
      try {
        if (userId) {
          window.localStorage.setItem(key, userId)
        } else {
          window.localStorage.removeItem(key)
        }
      } catch {
        // As above.
      }
    },
  }
}

export { posthog as posthogBrowser }
export type { PostHog as PostHogBrowserClient }
